import { recordingModeOf } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  Collapse,
  Container,
  Drawer,
  Menu,
  TextInput,
  UnstyledButton,
} from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import {
  IconCopy,
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconPencil,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { ExportMenu } from "../../../../../components/ExportMenu"
import { MiniPlayer } from "../../../../../components/Player/MiniPlayer"
import { RecordingModeChip } from "../../../../../components/RecordingModeChip"
import { ShareMeetingMenu } from "../../../../../components/ShareMeetingMenu"
import { SpeakerNamingStrip } from "../../../../../components/SpeakerNamingStrip"
import { StatusPill } from "../../../../../components/StatusPill"
import { buildLiteMeta } from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

export const MeetingDetailLiteView = ({
  title,
  onRename,
  startTime,
  durationMinutes,
  status,
  error,
  templateId,
  sourceLocale,
  segments,
  onCopyTranscript,
  copyPending = false,
  meetingId,
  companyId,
  canShare = false,
  onManageAccess,
  onToggleHidden,
  isHidden = false,
  onDelete,
  onUpgradeToFull,
  player,
  transcript,
  speakers,
  hasUnnamedSpeakers = false,
  unnamedCount = 0,
  firstUnnamedLabel,
  firstUnnamedShare,
  namingPeople = [],
  onNameClick,
  narrow,
  banner,
}: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(title)
  const [savingTitle, setSavingTitle] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [speakersOpen, setSpeakersOpen] = useState(false)
  // 62em is the Mantine `md` breakpoint. Below it the speaker list opens in place
  // and the player docks.
  const queriedNarrow = useMediaQuery("(max-width: 61.99em)", false, { getInitialValueInEffect: false })
  const isNarrow = narrow ?? queriedNarrow
  const meta = useMemo(
    () => buildLiteMeta(i18n, { startTime, durationMinutes, sourceLocale, segments }),
    [durationMinutes, i18n, segments, sourceLocale, startTime],
  )

  const beginTitleEdit = useCallback((): void => {
    setTitleDraft(title)
    setEditingTitle(true)
  }, [title])

  const cancelTitleEdit = useCallback((): void => {
    setTitleDraft(title)
    setEditingTitle(false)
  }, [title])

  const commitTitle = useCallback((): void => {
    const next = titleDraft.trim()
    if (!onRename || next.length === 0 || next === title) {
      cancelTitleEdit()
      return
    }
    setSavingTitle(true)
    void onRename(next)
      .then(() => {
        setEditingTitle(false)
      })
      .catch(() => {
        // The host already reports the failure. Leave the field open.
      })
      .finally(() => {
        setSavingTitle(false)
      })
  }, [cancelTitleEdit, onRename, title, titleDraft])

  const openSheet = useCallback((): void => {
    setSheetOpen(true)
  }, [])

  const closeSheet = useCallback((): void => {
    setSheetOpen(false)
  }, [])

  const handleName = useCallback((): void => {
    setSpeakersOpen(true)
    onNameClick?.()
  }, [onNameClick])

  const hasMore = Boolean(onManageAccess || onToggleHidden || onDelete || onUpgradeToFull)
  const unnamedTotal = unnamedCount > 0 ? unnamedCount : hasUnnamedSpeakers ? 1 : 0
  const showDock = isNarrow && Boolean(player)
  // A finished meeting has nothing left to report. "Lite" stays; "Transcribed" does not.
  const showStatus = status !== "summarized"
  const showLiteChip = recordingModeOf(templateId) === "lite"
  const metaChunks = [
    meta.dateLabel,
    meta.timeRange,
    durationMinutes > 0 ? meta.durationLabel : null,
    ...meta.languageParts,
  ].filter((part): part is string => Boolean(part))

  const speakerBody = (
    <>
      {speakers}
      {unnamedTotal > 0 ? (
        <p className={classes.namingHint}>
          <Trans>
            Play a sample, then name them. The transcript updates and the voice is remembered. Lite does not suggest
            names from the conversation — Full meetings do.
          </Trans>
        </p>
      ) : null}
    </>
  )

  return (
    <Container size="lg" py="md" data-testid="meeting-detail-lite">
      <div className={classes.page} data-dock={showDock ? "true" : undefined}>
        <Anchor component={Link} to="/" size="sm" className={classes.back}>
          &larr; <Trans>Meetings</Trans>
        </Anchor>
        {banner}
        {status === "error" ? (
          <Alert color="danger" variant="light">
            {error ?? (
              <Trans>This meeting failed to finish. Reprocess it from the card menu, or try again later.</Trans>
            )}
          </Alert>
        ) : null}

        <div className={classes.head}>
          <div className={classes.titleBlock}>
            <div className={classes.titleRow}>
              <h1 className={classes.title}>
                {editingTitle ? (
                  <TextInput
                    autoFocus
                    classNames={{ input: classes.titleInput }}
                    value={titleDraft}
                    disabled={savingTitle}
                    aria-label={i18n._(msg`Meeting title`)}
                    onChange={(event) => setTitleDraft(event.currentTarget.value)}
                    onBlur={commitTitle}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") commitTitle()
                      if (event.key === "Escape") cancelTitleEdit()
                    }}
                  />
                ) : onRename ? (
                  <UnstyledButton
                    type="button"
                    className={classes.titleButton}
                    title={i18n._(msg`Click to rename`)}
                    onClick={beginTitleEdit}
                  >
                    {title}
                    <IconPencil size={16} className={classes.titleEditIcon} aria-hidden />
                  </UnstyledButton>
                ) : (
                  title
                )}
              </h1>
            </div>
            <div className={classes.metaRow}>
              {metaChunks.map((chunk, i) => (
                <span key={`${i}-${chunk}`}>
                  {i > 0 ? <span className={classes.metaDot}> · </span> : null}
                  {chunk}
                </span>
              ))}
              {showStatus ? (
                <span>
                  {metaChunks.length > 0 ? <span className={classes.metaDot}> · </span> : null}
                  <StatusPill phase={status} lite />
                </span>
              ) : null}
              {showLiteChip ? (
                <span>
                  {metaChunks.length > 0 || showStatus ? <span className={classes.metaDot}> · </span> : null}
                  <RecordingModeChip mode="lite" />
                </span>
              ) : null}
            </div>
          </div>

          <div className={classes.actions}>
            <Button
              variant="default"
              leftSection={<IconCopy size={16} />}
              onClick={onCopyTranscript}
              loading={copyPending}
              disabled={!companyId}
            >
              <Trans>Copy transcript</Trans>
            </Button>
            {companyId && canShare ? <ShareMeetingMenu meetingId={meetingId} companyId={companyId} /> : null}
            {companyId ? <ExportMenu meetingId={meetingId} companyId={companyId} variant="transcript-only" /> : null}
            {hasMore ? (
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <ActionIcon variant="default" size="lg" aria-label={i18n._(msg`More actions`)}>
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {onUpgradeToFull ? (
                    <Menu.Item onClick={onUpgradeToFull}>
                      <Trans>Upgrade to Full…</Trans>
                    </Menu.Item>
                  ) : null}
                  {onManageAccess ? (
                    <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={onManageAccess}>
                      <Trans>Manage access</Trans>
                    </Menu.Item>
                  ) : null}
                  {onToggleHidden ? (
                    <Menu.Item
                      leftSection={isHidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                      onClick={onToggleHidden}
                    >
                      {isHidden ? <Trans>Unhide</Trans> : <Trans>Hide</Trans>}
                    </Menu.Item>
                  ) : null}
                  {onDelete ? (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconTrash size={14} />} color="danger" onClick={onDelete}>
                        <Trans>Delete permanently</Trans>
                      </Menu.Item>
                    </>
                  ) : null}
                </Menu.Dropdown>
              </Menu>
            ) : null}
          </div>
        </div>

        {unnamedTotal > 0 ? (
          <SpeakerNamingStrip
            unnamedCount={unnamedTotal}
            firstUnnamedLabel={firstUnnamedLabel}
            firstUnnamedShare={firstUnnamedShare}
            people={namingPeople}
            onNameClick={handleName}
          />
        ) : null}

        {isNarrow && unnamedTotal === 0 && namingPeople.length > 0 ? (
          <div className={classes.namedStrip}>
            <SpeakerNamingStrip unnamedCount={0} people={namingPeople} onNameClick={handleName} />
            <button
              type="button"
              className={classes.namedStripHit}
              aria-expanded={speakersOpen}
              aria-label={i18n._(msg`Who spoke`)}
              onClick={handleName}
            />
          </div>
        ) : null}

        {isNarrow && (unnamedTotal > 0 || namingPeople.length > 0) ? (
          <Collapse in={speakersOpen}>
            {speakersOpen ? (
              <div className={classes.speakersCol} data-testid="lite-speakers">
                {speakerBody}
              </div>
            ) : null}
          </Collapse>
        ) : null}

        {showDock ? (
          <Drawer
            opened={sheetOpen}
            onClose={closeSheet}
            position="bottom"
            keepMounted
            size="auto"
            title={<Trans>Recording</Trans>}
            classNames={{ body: classes.playerSheet }}
          >
            {player}
          </Drawer>
        ) : (
          player
        )}

        <div className={classes.body}>
          <div className={classes.transcriptCol}>{transcript}</div>
          {isNarrow ? null : (
            <aside className={classes.speakersCol} data-testid="lite-speakers">
              {speakerBody}
            </aside>
          )}
        </div>

        {showDock ? <MiniPlayer onExpand={openSheet} /> : null}
      </div>
    </Container>
  )
}
